/**
 * Public visitor self-check-in (no login). Two modes, chosen by the route:
 *
 *   /checkin/:code  — invited. We GET a small prefill from the pass short
 *                     code (guest name + which villa), the guest adds their
 *                     vehicle number, and we POST it. They arrive pre-approved
 *                     (the resident approved by issuing the pass); the guard
 *                     just verifies the last 4 of the plate.
 *   /checkin        — walk-up. An uninvited guest self-registers against a
 *                     villa number → PENDING; the resident gets a push to
 *                     approve before the guard admits.
 *
 * Anonymous: every call goes through `apiFetch` with no auth (the
 * `/api/public/checkin*` endpoints are unauthenticated, captcha- and
 * rate-limited). Mirrors the public ContactForm pattern.
 */
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { apiFetch } from "../auth/apiClient";
import TurnstileWidget from "../captcha/TurnstileWidget";

const KINDS = [
  { value: "GUEST", label: "Guest" },
  { value: "DELIVERY", label: "Delivery" },
  { value: "CAB", label: "Cab / Taxi" },
  { value: "SERVICE", label: "Service visit" },
  { value: "OTHER", label: "Other" },
];

const wrap = {
  maxWidth: 460,
  margin: "48px auto",
  padding: "0 16px",
};
const card = {
  background: "#fff",
  border: "1px solid #e6ebe6",
  borderRadius: 16,
  padding: 24,
  boxShadow: "0 6px 24px rgba(20, 40, 20, 0.06)",
};
const label = {
  display: "block",
  fontSize: 13,
  fontWeight: 600,
  color: "#3a463a",
  margin: "14px 0 6px",
};
const input = {
  width: "100%",
  boxSizing: "border-box",
  padding: "11px 12px",
  fontSize: 15,
  border: "1px solid #cfd8cf",
  borderRadius: 10,
  outline: "none",
};
const button = (disabled) => ({
  width: "100%",
  marginTop: 18,
  padding: "13px 16px",
  fontSize: 16,
  fontWeight: 700,
  color: "#fff",
  background: disabled ? "#9bb39b" : "#2f7d32",
  border: "none",
  borderRadius: 12,
  cursor: disabled ? "not-allowed" : "pointer",
});

export default function VisitorCheckInPage() {
  const { code } = useParams();
  const invited = !!code;

  // Invited-mode prefill (guest name + villa) resolved from the short code.
  const [prefill, setPrefill] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [loadingPrefill, setLoadingPrefill] = useState(invited);

  // Form state (superset of both modes).
  const [villaNumber, setVillaNumber] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [vehicleNo, setVehicleNo] = useState("");
  const [kind, setKind] = useState("GUEST");
  const [purpose, setPurpose] = useState("");

  const [captchaToken, setCaptchaToken] = useState("");
  const [captchaResetKey, setCaptchaResetKey] = useState(0);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(null);

  useEffect(() => {
    if (!invited) return;
    let cancelled = false;
    setLoadingPrefill(true);
    apiFetch(`/api/public/checkin/${encodeURIComponent(code)}`)
      .then((data) => {
        if (cancelled) return;
        setPrefill(data);
      })
      .catch((e) => {
        if (cancelled) return;
        setLoadError(
          e && e.message
            ? e.message
            : "This pass link is invalid or has expired.",
        );
      })
      .finally(() => !cancelled && setLoadingPrefill(false));
    return () => {
      cancelled = true;
    };
  }, [invited, code]);

  const resetCaptcha = useCallback(() => {
    setCaptchaToken("");
    setCaptchaResetKey((k) => k + 1);
  }, []);

  const submit = useCallback(
    async (e) => {
      e.preventDefault();
      setError("");
      if (!captchaToken) {
        setError("Please complete the captcha first.");
        return;
      }
      setSubmitting(true);
      try {
        let ack;
        if (invited) {
          ack = await apiFetch(`/api/public/checkin/${encodeURIComponent(code)}`, {
            method: "POST",
            body: {
              vehicle_no: vehicleNo.trim() || null,
              phone: phone.trim() || null,
              purpose: purpose.trim() || null,
              captcha_token: captchaToken,
            },
          });
        } else {
          ack = await apiFetch(`/api/public/checkin`, {
            method: "POST",
            body: {
              villa_number: villaNumber.trim(),
              name: name.trim(),
              phone: phone.trim() || null,
              vehicle_no: vehicleNo.trim() || null,
              kind,
              purpose: purpose.trim() || null,
              captcha_token: captchaToken,
            },
          });
        }
        setDone(ack);
      } catch (err) {
        setError(err && err.message ? err.message : "Could not check you in.");
        resetCaptcha();
      } finally {
        setSubmitting(false);
      }
    },
    [
      invited,
      code,
      vehicleNo,
      phone,
      purpose,
      villaNumber,
      name,
      kind,
      captchaToken,
      resetCaptcha,
    ],
  );

  // ---- success ----
  if (done) {
    const approved = done.status === "APPROVED";
    return (
      <div style={wrap}>
        <div style={card}>
          <h2 style={{ marginTop: 0, color: "#2f7d32" }}>
            {approved ? "You're checked in ✅" : "Almost there"}
          </h2>
          <p style={{ color: "#3a463a", lineHeight: 1.5 }}>{done.message}</p>
          {done.plate_last4 ? (
            <p style={{ color: "#3a463a" }}>
              At the gate, the guard will confirm your plate ending in{" "}
              <strong style={{ fontSize: 18, color: "#2f7d32" }}>
                {done.plate_last4}
              </strong>
              .
            </p>
          ) : null}
          {done.villa_number ? (
            <p style={{ color: "#6b766b", fontSize: 14 }}>
              Visiting {done.villa_number}.
            </p>
          ) : null}
        </div>
      </div>
    );
  }

  // ---- invited link broken / expired ----
  if (invited && loadError) {
    return (
      <div style={wrap}>
        <div style={card}>
          <h2 style={{ marginTop: 0 }}>Pass unavailable</h2>
          <p style={{ color: "#3a463a" }}>{loadError}</p>
          <p style={{ color: "#6b766b", fontSize: 14 }}>
            Ask the resident to share a fresh invite, or check in at the gate.
          </p>
        </div>
      </div>
    );
  }

  if (invited && loadingPrefill) {
    return (
      <div style={wrap}>
        <div style={card}>
          <p style={{ color: "#6b766b" }}>Loading your invite…</p>
        </div>
      </div>
    );
  }

  // ---- form ----
  return (
    <div style={wrap}>
      <div style={card}>
        <h2 style={{ marginTop: 0, color: "#1f2a1f" }}>
          {invited ? "Check in for your visit" : "Visitor check-in"}
        </h2>
        {invited && prefill ? (
          <p style={{ color: "#3a463a", marginTop: -4 }}>
            Hi {prefill.guest_name}! You're expected at{" "}
            <strong>{prefill.villa_number}</strong>. Add your vehicle number so
            the gate can wave you straight through.
          </p>
        ) : (
          <p style={{ color: "#3a463a", marginTop: -4 }}>
            Not pre-invited? Enter the villa you're visiting and your details —
            the resident will get a request to approve you.
          </p>
        )}

        <form onSubmit={submit}>
          {!invited ? (
            <>
              <label style={label}>Villa number</label>
              <input
                style={input}
                value={villaNumber}
                onChange={(e) => setVillaNumber(e.target.value)}
                placeholder="e.g. A-101"
                required
              />
              <label style={label}>Your name</label>
              <input
                style={input}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Full name"
                required
              />
              <label style={label}>Reason for visit</label>
              <select
                style={input}
                value={kind}
                onChange={(e) => setKind(e.target.value)}
              >
                {KINDS.map((k) => (
                  <option key={k.value} value={k.value}>
                    {k.label}
                  </option>
                ))}
              </select>
            </>
          ) : null}

          <label style={label}>Vehicle number {invited ? "" : "(if any)"}</label>
          <input
            style={input}
            value={vehicleNo}
            onChange={(e) => setVehicleNo(e.target.value.toUpperCase())}
            placeholder="e.g. TS09AB1234"
            autoCapitalize="characters"
          />

          <label style={label}>Phone (optional)</label>
          <input
            style={input}
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="Mobile number"
            inputMode="tel"
          />

          <div style={{ marginTop: 16 }}>
            <TurnstileWidget
              onToken={setCaptchaToken}
              onError={resetCaptcha}
              onExpire={resetCaptcha}
              resetKey={captchaResetKey}
            />
          </div>

          {error ? (
            <p style={{ color: "#c0392b", fontSize: 14, marginTop: 12 }}>
              {error}
            </p>
          ) : null}

          <button
            type="submit"
            style={button(submitting || !captchaToken)}
            disabled={submitting || !captchaToken}
          >
            {submitting ? "Checking in…" : "Check in"}
          </button>
        </form>
      </div>
    </div>
  );
}
